/**
 * Product Backlog Template Form
 * Agile product backlog management
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const ProductBacklogForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    project_name: '', product_owner: '', date: new Date().toISOString().split('T')[0],
    backlog_items: [{ id: '', type: 'User Story', title: '', description: '', acceptance_criteria: '', priority: 'Medium', status: 'Backlog', story_points: 0, sprint: '', epic: '', assignee: '', created_date: '', notes: '' }],
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    project_name: 'E-Commerce Platform v2.0',
    product_owner: 'Sarah Johnson',
    date: new Date().toISOString().split('T')[0],
    backlog_items: [
      { id: 'US-101', type: 'User Story', title: 'User Login with SSO', description: 'As a user, I want to login with my Google account so that I don\'t need to remember another password', acceptance_criteria: '- Google OAuth works\n- User profile synced\n- Session persists', priority: 'Critical', status: 'In Sprint', story_points: 8, sprint: 'Sprint 5', epic: 'Authentication', assignee: 'John Smith', created_date: '2025-01-01', notes: '' },
      { id: 'US-102', type: 'User Story', title: 'Shopping Cart Persistence', description: 'As a user, I want my cart to be saved so I can continue shopping later', acceptance_criteria: '- Cart syncs to backend\n- Works across devices\n- Guest carts merge on login', priority: 'High', status: 'In Sprint', story_points: 5, sprint: 'Sprint 5', epic: 'Cart', assignee: 'Jane Doe', created_date: '2025-01-02', notes: '' },
      { id: 'US-103', type: 'User Story', title: 'Order Tracking Dashboard', description: 'As a user, I want to see real-time order status with delivery tracking', acceptance_criteria: '- Shows order timeline\n- Integrates with carriers\n- Push notifications', priority: 'High', status: 'Ready', story_points: 13, sprint: '', epic: 'Orders', assignee: '', created_date: '2025-01-03', notes: 'Needs carrier API keys' },
      { id: 'US-104', type: 'User Story', title: 'Product Reviews & Ratings', description: 'As a user, I want to read and write product reviews', acceptance_criteria: '- 5-star rating system\n- Text reviews with photos\n- Helpful votes', priority: 'Medium', status: 'Done', story_points: 8, sprint: 'Sprint 4', epic: 'Products', assignee: 'Alex Kumar', created_date: '2025-01-04', notes: 'Completed' },
      { id: 'BUG-201', type: 'Bug', title: 'Cart total not updating', description: 'Cart total doesn\'t update when quantity changes', acceptance_criteria: '- Total updates immediately\n- No page refresh needed', priority: 'High', status: 'In Sprint', story_points: 3, sprint: 'Sprint 5', epic: 'Cart', assignee: 'Jane Doe', created_date: '2025-01-10', notes: '' },
      { id: 'TECH-301', type: 'Technical', title: 'Migrate to PostgreSQL 15', description: 'Upgrade database from v14 to v15 for performance improvements', acceptance_criteria: '- Zero downtime migration\n- All tests pass\n- Performance verified', priority: 'Medium', status: 'Backlog', story_points: 8, sprint: '', epic: 'Infrastructure', assignee: '', created_date: '2025-01-05', notes: 'Plan during Q2' },
      { id: 'US-105', type: 'User Story', title: 'Wishlist Feature', description: 'As a user, I want to save items to a wishlist for later', acceptance_criteria: '- Add/remove from wishlist\n- Share wishlist\n- Move to cart', priority: 'Low', status: 'Backlog', story_points: 5, sprint: '', epic: 'User Features', assignee: '', created_date: '2025-01-06', notes: '' }
    ],
    rai_assignments: [
      { task: 'Groom backlog weekly', responsible: 'Product Owner', accountable: 'Scrum Master', informed: 'Dev Team', due_date: '2025-01-17', status: 'In Progress' }
    ],
    followups: [
      { item: 'Prioritize Q2 features', owner: 'Product Owner', due_date: '2025-02-01', priority: 'High', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Product Backlog Draft";
      const payload = {
        name: draftName,
        template_type: "Product Backlog",
        template_id: "product-backlog",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "product-backlog", "Product Backlog", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("product-backlog", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/product-backlog`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Product_Backlog_${formData.project_name.replace(/\s+/g, '_') || 'Document'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Product Backlog",
        formData,
        settings: activeSettings || {},
        templateType: "Product Backlog",
        templateId: "product-backlog",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addItem = () => {
    const count = formData.backlog_items.length + 1;
    setFormData({
      ...formData,
      backlog_items: [...formData.backlog_items, { id: `US-${100 + count}`, type: 'User Story', title: '', description: '', acceptance_criteria: '', priority: 'Medium', status: 'Backlog', story_points: 0, sprint: '', epic: '', assignee: '', created_date: new Date().toISOString().split('T')[0], notes: '' }]
    });
  };

  const updateItem = (index, field, value) => {
    const items = [...formData.backlog_items];
    items[index] = { ...items[index], [field]: value };
    setFormData({ ...formData, backlog_items: items });
  };

  const removeItem = (index) => {
    setFormData({ ...formData, backlog_items: formData.backlog_items.filter((_, i) => i !== index) });
  };

  const types = ['User Story', 'Bug', 'Technical', 'Spike', 'Epic'];
  const priorities = ['Critical', 'High', 'Medium', 'Low'];
  const statuses = ['Backlog', 'Ready', 'In Sprint', 'In Progress', 'Done', 'Blocked'];

  const totalPoints = formData.backlog_items.reduce((sum, i) => sum + (i.story_points || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Product Backlog</h1><p className="text-sm text-gray-500">Agile product backlog</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Product Backlog</CardTitle>
            <div className="flex gap-4 mt-2">
              <div className="bg-blue-100 rounded-lg px-3 py-1"><span className="text-xs text-gray-600">Items:</span> <span className="font-bold text-blue-700">{formData.backlog_items.length}</span></div>
              <div className="bg-green-100 rounded-lg px-3 py-1"><span className="text-xs text-gray-600">Total Points:</span> <span className="font-bold text-green-700">{totalPoints}</span></div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div><Label>Project Name *</Label><Input value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} placeholder="Project name" /></div>
              <div><Label>Product Owner</Label><Input value={formData.product_owner} onChange={(e) => setFormData({...formData, product_owner: e.target.value})} placeholder="Name" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Backlog Items</Label>
              <div className="space-y-3 mt-3">
                {formData.backlog_items.map((item, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border ${item.status === 'Done' ? 'bg-green-50' : item.status === 'In Sprint' ? 'bg-blue-50' : 'bg-gray-50'}`}>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                      <div><Label className="text-xs">ID</Label><Input value={item.id} onChange={(e) => updateItem(idx, 'id', e.target.value)} placeholder="US-101" className="text-sm font-mono" /></div>
                      <div><Label className="text-xs">Type</Label>
                        <Select value={item.type} onValueChange={(v) => updateItem(idx, 'type', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-2"><Label className="text-xs">Title *</Label><Input value={item.title} onChange={(e) => updateItem(idx, 'title', e.target.value)} placeholder="User story title" className="text-sm" /></div>
                      <div><Label className="text-xs">Points</Label><Input type="number" value={item.story_points} onChange={(e) => updateItem(idx, 'story_points', parseInt(e.target.value) || 0)} className="text-sm" /></div>
                      <div className="flex items-end"><Button variant="ghost" size="icon" onClick={() => removeItem(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button></div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
                      <div><Label className="text-xs">Priority</Label>
                        <Select value={item.priority} onValueChange={(v) => updateItem(idx, 'priority', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Status</Label>
                        <Select value={item.status} onValueChange={(v) => updateItem(idx, 'status', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Sprint</Label><Input value={item.sprint} onChange={(e) => updateItem(idx, 'sprint', e.target.value)} placeholder="Sprint 5" className="text-sm" /></div>
                      <div><Label className="text-xs">Epic</Label><Input value={item.epic} onChange={(e) => updateItem(idx, 'epic', e.target.value)} placeholder="Epic name" className="text-sm" /></div>
                      <div><Label className="text-xs">Assignee</Label><Input value={item.assignee} onChange={(e) => updateItem(idx, 'assignee', e.target.value)} placeholder="Name" className="text-sm" /></div>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addItem} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Item</Button>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ProductBacklogForm;
